import React, { useContext, useEffect } from 'react';
import * as Yup from 'yup';
import { LeReacteurContext } from '../../Layout';
import MoonLoader from 'react-spinners/MoonLoader';
/* --- import components ----*/
import { BackButton, ErrorMessage, MultiSelectSmall } from '../../Form';
import { Form, Formik } from 'formik';
import { FormContainer, Wrapper } from '../elements';
import { InputForm, Label, NextBtn, Sublabel, TextareaForm } from '../../Form/elements';
import { Paragraph } from 'src/components/Typography';
import ConditionalFormPart from '../ConditionalFormPart';
import CheckboxComp from 'src/components/EmailForm/CheckboxComp';
/* --- import utils ----*/
import { useRestoreFormManager } from 'src/utils/RestoreForm';
/* --- import style ----*/
import colors from 'src/style/colors';

const FORM_TOKEN = 'apply-step3';

const ThirdBlock = ({ onBackClicked, onSubmit, error, GRCtoken, isLoading, stepsData }) => {
  const ThirdBlockSchema = Yup.object().shape({
    question1: Yup.string(),
    question2: Yup.string().when('question1', {
      is: 'Oui',
      then: Yup.string().required('Champ obligatoire'),
    }),
    question3: Yup.string().required('Champ obligatoire'),
    why: Yup.string().required('Champ obligatoire'),
    optIn: Yup.string().required('Veuillez cocher cette case'),
    handicap: Yup.string(),
    hasRncp5Graduation:
      stepsData.step1.certification === 'RNCP6'
        ? Yup.boolean().required('Champ obligatoire')
        : Yup.boolean().notRequired(),
  });

  const { dtlVirtualPageView } = useContext(LeReacteurContext);
  const manager = useRestoreFormManager();
  const initialValues = {
    question1: '',
    question2: '',
    question3: '',
    why: '',
    optIn: '',
    handicap: '',
    hasRncp5Graduation: '',
    ...(manager.get(FORM_TOKEN) || {}),
  };

  if (stepsData.step1.certification === 'RNCP5') {
    initialValues.hasRncp5Graduation = false;
  }

  useEffect(() => {
    if (window) {
      window.scrollTo({
        top: 90,
        left: 0,
        behavior: 'smooth',
      });
    } else {
      return null;
    }
  });
  useEffect(() => {
    dtlVirtualPageView('bootcamp', '3', 'Pré-requis');
  }, []);

  return (
    <FormContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={ThirdBlockSchema}
        validateOnMount={true}
        onSubmit={(values) => {
          const { why, optIn, handicap, hasRncp5Graduation } = values;
          manager.set(FORM_TOKEN, values);
          const questions = {
            poleEmploi: values.question1,
            poleEmploiId: values.question2,
            postalCode: values.question3,
            why,
            optIn,
            handicap,
            hasRncp5Graduation,
          };
          onSubmit(questions);
          window.dataLayer.push({
            event: 'bootcampPrerequisite',
            optIn,
          });
        }}
      >
        {({ errors, handleChange, setFieldValue, touched, values, isSubmitting }) => {
          const isDisabled = () => {
            try {
              ThirdBlockSchema.validateSync(values);
              return false;
            } catch (error) {
              return true;
            }
          };
          return (
            <Form
              css={{
                maxWidth: isSubmitting ? '' : 546,
                width: isSubmitting ? 'none' : '100%',
                minWidth: isSubmitting ? '100%' : '',
                margin: isSubmitting ? 'auto' : '',
              }}
            >
              {isLoading ? (
                <Wrapper>
                  <MoonLoader color={colors.purple} size={20} />
                </Wrapper>
              ) : error ? (
                <Wrapper>
                  <Paragraph css={{ padding: 0 }}>
                    Une erreur est survenue, merci de réessayer ultérieurement ou de nous contacter
                    si le problème persiste.
                  </Paragraph>
                </Wrapper>
              ) : (
                <>
                  {/* Question 1 */}
                  <Label>Êtes-vous inscrit(e) à France Travail (ex Pôle Emploi) ?</Label>
                  <Sublabel>
                    Nous aimerions savoir si vous êtes potentiellement éligible à un financement.
                  </Sublabel>
                  <div css={{ display: 'flex' }}>
                    <MultiSelectSmall
                      isSelected={values.question1 === 'Oui'}
                      onClick={() => {
                        setFieldValue('question1', values.question1 !== 'Oui' ? 'Oui' : '');
                      }}
                    >
                      Oui
                    </MultiSelectSmall>
                    <MultiSelectSmall
                      isLeft
                      isSelected={values.question1 === 'Non'}
                      onClick={() => {
                        setFieldValue('question1', values.question1 !== 'Non' ? 'Non' : '');
                      }}
                    >
                      Non
                    </MultiSelectSmall>
                  </div>
                  <ErrorMessage
                    isVisible={errors.question1 && touched.question1}
                    message={errors.question1}
                  />

                  {/* Question 2 */}
                  <ConditionalFormPart
                    isVisible={values.question1 === 'Oui'}
                    onVisibleChange={() => {
                      if (values.question1 === 'Non') {
                        setFieldValue('question2', '');
                      }
                    }}
                  >
                    <Label css={{ marginTop: 50, paddingBottom: 8 }}>
                      Quel est votre identifiant France Travail ?
                    </Label>
                    <Sublabel>
                      Nous aimerions savoir si vous êtes potentiellement éligible à un financement.
                    </Sublabel>
                    <InputForm
                      name="question2"
                      onChange={handleChange}
                      value={values.question2}
                      errorStyle={errors.question2 && touched.question2 ? true : false}
                    />
                    <ErrorMessage
                      isVisible={errors.question2 && touched.question2}
                      message={errors.question2}
                    />
                  </ConditionalFormPart>

                  {/* Question 3 */}
                  <Label css={{ marginTop: 50 }}>Quel est votre code postal ?</Label>
                  <InputForm
                    name="question3"
                    onChange={(e) => {
                      const newValue = e.target.value.replace(/[^0-9]/g, '');
                      setFieldValue('question3', newValue);
                    }}
                    errorStyle={errors.question3 && touched.question3 ? true : false}
                    value={values.question3}
                  />
                  <ErrorMessage
                    isVisible={errors.question3 && touched.question3}
                    message={errors.question3}
                  />

                  <Label css={{ marginTop: 50, paddingBottom: 5 }}>
                    Pourquoi souhaitez-vous suivre une formation de ce type ?
                  </Label>
                  <Sublabel>
                    Expliquez-nous votre intérêt pour cette formation, nous sommes curieux d'en
                    savoir plus sur votre motivation.
                  </Sublabel>
                  <TextareaForm
                    type="why"
                    name="why"
                    errorStyle={errors.why && touched.why ? true : false}
                    value={values.why}
                    onChange={handleChange}
                  />
                  <ErrorMessage isVisible={errors.why && touched.why} message={errors.why} />

                  {/* has RNCP5  */}
                  {stepsData.step1.certification === 'RNCP6' ? (
                    <>
                      <Label css={{ marginTop: 50 }}>
                        Êtes-vous déjà titulaire d’un titre RNCP de niveau 5 (Bac+2) dans le domaine
                        du développement ?
                      </Label>
                      <div data-cy="rncp5" css={{ display: 'flex' }}>
                        <MultiSelectSmall
                          isSelected={values.hasRncp5Graduation}
                          onClick={() => {
                            setFieldValue('hasRncp5Graduation', true);
                          }}
                        >
                          Oui
                        </MultiSelectSmall>
                        <MultiSelectSmall
                          isLeft
                          isSelected={values.hasRncp5Graduation === false}
                          onClick={() => {
                            setFieldValue('hasRncp5Graduation', false);
                          }}
                        >
                          Non
                        </MultiSelectSmall>
                      </div>
                      <ErrorMessage
                        isVisible={errors.hasRncp5Graduation && touched.hasRncp5Graduation}
                        message={errors.hasRncp5Graduation}
                      />
                    </>
                  ) : null}

                  {/* handicap  */}
                  <Label css={{ marginTop: 50 }}>Avez-vous des besoins spécifiques ?</Label>
                  <Sublabel>
                    Par exemple, des besoins liés à une situation de handicap. Nous vérifierons
                    ensemble comment vous proposer l'accompagnement le plus adapté possible.
                  </Sublabel>

                  <div data-cy="needs" css={{ display: 'flex' }}>
                    <MultiSelectSmall
                      isSelected={values.handicap === 'Oui'}
                      onClick={() => {
                        setFieldValue('handicap', values.handicap !== 'Oui' ? 'Oui' : '');
                      }}
                    >
                      Oui
                    </MultiSelectSmall>
                    <MultiSelectSmall
                      isLeft
                      isSelected={values.handicap === 'Non'}
                      onClick={() => {
                        setFieldValue('handicap', values.handicap !== 'Non' ? 'Non' : '');
                      }}
                    >
                      Non
                    </MultiSelectSmall>
                  </div>
                  <ErrorMessage
                    isVisible={errors.handicap && touched.handicap}
                    message={errors.handicap}
                  />

                  {/* OPTIN  */}
                  <div css={{ marginTop: 50 }}>
                    <CheckboxComp
                      text="J'accepte que Le Reacteur me recontacte par mail."
                      value={values.optIn === 'Oui' ? true : false}
                      onChange={() => {
                        setFieldValue('optIn', values.optIn !== 'Oui' ? 'Oui' : '');
                      }}
                      errorDisabled={false}
                      errorIsVisible={errors.optIn && touched.optIn}
                      errorMessage={errors.optIn}
                    />
                  </div>
                </>
              )}

              {/* SUBMIT  */}
              <div
                css={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: isSubmitting ? 'center' : 'space-between',
                }}
              >
                {isSubmitting ? null : (
                  <BackButton
                    onClick={() => {
                      manager.set(FORM_TOKEN, values);
                      onBackClicked();
                    }}
                  />
                )}
                <NextBtn
                  type="submit"
                  disabledStyle={isSubmitting || isDisabled()}
                  disabled={isSubmitting}
                >
                  Envoyer
                </NextBtn>
              </div>
            </Form>
          );
        }}
      </Formik>
      {/* <PerksWrapper>
        <Perks />
      </PerksWrapper> */}
    </FormContainer>
  );
};

export default ThirdBlock;
